import React, { useEffect } from "react";
import { XMarkIcon, PhotoIcon } from "@heroicons/react/24/outline";
import SearchableMemberSelect from "../common/SearchableMemberSelect";
import useDamageForm from "../../hooks/useDamageForm";

export default function DamageModal({
    open,
    onClose = () => { },
    book,
    member,
    members = [],
    onSave = () => { },
    onMemberResolve,
}) {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const {
        memberId,
        setMemberId,
        orderId,
        setOrderId,
        reason,
        setReason,
        severity,
        setSeverity,
        copyId,
        setCopyId,
        date,
        setDate,
        amount,
        setAmount,
        remark,
        setRemark,
        sendEmail,
        setSendEmail,
        imagesBase64,
        orderIds,
        damageHistory,
        ordersLoading,
        copiesLoading,
        historyLoading,
        saving,
        error: saveError,
        copyOptions,
        handleImageUpload,
        handleSaveDamage,
        resetForm,
    } = useDamageForm({
        book,
        token,
        initialMemberId: member?.id,
        initialOrderId: book?.added_order_id || book?.order_id || book?.activeOrder?.added_order_id,
        onSaveSuccess: onSave,
    });

    // Reset when modal opens/closes
    useEffect(() => {
        if (!open) {
            resetForm();
        } else {
            if (member?.id) {
                setMemberId(String(member.id));
            }
        }
    }, [open, member, setMemberId, resetForm]);

    if (!open) return null;

    const selectedMember = members.find((m) => String(m.id) === String(memberId));

    return (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-6xl rounded-lg shadow-lg overflow-hidden">
                {/* HEADER */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="text-xl font-semibold">Add Damage</h2>
                    <button onClick={onClose} className="p-2 rounded hover:bg-gray-100">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* BODY */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                    {/* LEFT FORM */}
                    <div className="p-6 border-r">
                        {/* BOOK INFO */}
                        <div className="flex gap-4 pb-5 border-b">
                            <img
                                src={book?.img || "https://via.placeholder.com/120x180"}
                                alt={book?.title}
                                className="w-20 h-28 object-cover rounded"
                            />
                            <div className="flex-1">
                                <h3 className="font-semibold text-lg">{book?.title || "-"}</h3>
                                <p className="text-blue-600 text-sm">
                                    {book?.credits ? `${book.credits} Credits` : "—"}
                                </p>
                                <p className="text-gray-500 text-sm">
                                    {book?.stock_status === "outofstock" ? "Out of stock" : "Available"}
                                </p>
                                <p className="text-gray-400 text-xs mt-1">ID: {book?.id}</p>
                            </div>

                            {/* REUSABLE MEMBER SELECT */}
                            <div className="w-[260px] relative">
                                <p className="text-xs font-semibold text-gray-500 mb-1">
                                    Member ID / Name
                                </p>
                                <SearchableMemberSelect
                                    members={members}
                                    value={memberId}
                                    onChange={(id, memberObj) => {
                                        setMemberId(id);
                                        if (memberObj && onMemberResolve) {
                                            onMemberResolve(memberObj);
                                        }
                                    }}
                                    placeholder="Search member..."
                                />
                                <div className="mt-2 text-xs text-gray-400">
                                    Selected:{" "}
                                    <span className="text-gray-700 font-medium">
                                        {selectedMember?.name || "None"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* FORM GRID */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                            {/* Order ID */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500 flex items-center">
                                    Order ID
                                    {ordersLoading && <span className="ml-2 text-[10px] text-gray-400 animate-pulse">(Loading...)</span>}
                                </label>
                                <select
                                    value={orderId}
                                    onChange={(e) => setOrderId(e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                                >
                                    <option value="">Select Order</option>
                                    {orderIds.map((id) => (
                                        <option key={id} value={id}>
                                            Order #{id}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Reason */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500">
                                    Reason
                                </label>
                                <select
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                                >
                                    <option value="">Select</option>
                                    <option value="Ripped Pages">Ripped Pages</option>
                                    <option value="Liquid Damage">Liquid Damage</option>
                                    <option value="Book Lost">Book Lost</option>
                                    <option value="Other Damages">Other Damages</option>
                                </select>
                            </div>

                            {/* Severity */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500">
                                    Severity
                                </label>
                                <select
                                    value={severity}
                                    onChange={(e) => setSeverity(e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                                >
                                    <option value="">Select</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                </select>
                            </div>

                            {/* Copy */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500 flex items-center">
                                    Copy
                                    {copiesLoading && <span className="ml-2 text-[10px] text-gray-400 animate-pulse">(Loading...)</span>}
                                </label>
                                <select
                                    value={copyId}
                                    onChange={(e) => setCopyId(e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                                >
                                    {copyOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Date */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="w-full border rounded-md px-3 py-2 text-sm"
                                />
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500">
                                    Amount
                                </label>
                                <input
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="₹ Amount"
                                    className="w-full border rounded-md px-3 py-2 text-sm"
                                />
                            </div>

                            {/* Remark */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500">
                                    Remark
                                </label>
                                <input
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    placeholder="Remarks"
                                    className="w-full border rounded-md px-3 py-2 text-sm"
                                />
                            </div>
                        </div>

                        {/* EMAIL + UPLOAD */}
                        <div className="flex items-center justify-between mt-5">
                            <label className="flex items-center gap-2 text-sm text-gray-600">
                                <input
                                    type="checkbox"
                                    checked={sendEmail}
                                    onChange={(e) => setSendEmail(e.target.checked)}
                                />
                                Send E-Mail Notification
                            </label>

                            <div className="flex items-center gap-3">
                                <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm hover:bg-gray-50">
                                    <PhotoIcon className="h-5 w-5" />
                                    Upload Images
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleImageUpload(e.target.files)}
                                        className="hidden"
                                    />
                                </label>
                                <span className="text-xs text-gray-400">
                                    {imagesBase64.length ? `${imagesBase64.length} added` : ""}
                                </span>
                            </div>
                        </div>

                        {/* ERROR */}
                        {saveError && <p className="text-sm text-red-500 mt-4">{saveError}</p>}

                        {/* FOOTER BUTTON */}
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={handleSaveDamage}
                                disabled={saving}
                                className={`px-6 py-2 rounded-md text-white text-sm ${saving ? "bg-gray-400" : "bg-black hover:bg-gray-900"}`}
                            >
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>

                    {/* RIGHT HISTORY */}
                    <div className="p-6">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">
                            Damage History
                        </h3>

                        {historyLoading && (
                            <p className="text-sm text-gray-400">Loading damage history…</p>
                        )}

                        {!historyLoading && damageHistory.length === 0 && (
                            <p className="text-sm text-gray-400">No damage history found.</p>
                        )}

                        <div className="space-y-4 max-h-[520px] overflow-y-auto pr-2">
                            {damageHistory.map((d, idx) => (
                                <div key={idx} className="border rounded-lg p-4 bg-white shadow-sm">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-sm font-semibold">
                                                Member Name: {selectedMember?.name || "-"}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                Phone No: {selectedMember?.phone || "-"}
                                            </p>
                                        </div>
                                        <p className="text-xs font-semibold">
                                            Member Id: {selectedMember?.memberId || "-"}
                                        </p>
                                    </div>

                                    <div className="mt-2 text-xs text-gray-500 space-y-1">
                                        <p>Date: {d.date}</p>
                                        <p>Remark: {d.remark || "-"}</p>
                                        <p>Type: {d.type || "-"}</p>
                                        <p>Severity: {d.severity || "-"}</p>
                                        <p className="font-semibold text-black">
                                            Amount: ₹{d.amt || 0}
                                        </p>
                                    </div>

                                    {/* Images */}
                                    {Array.isArray(d.images) && d.images.length > 0 && (
                                        <div className="flex gap-2 mt-3 flex-wrap">
                                            {d.images.map((img, i) => (
                                                <img
                                                    key={i}
                                                    src={img}
                                                    alt="damage"
                                                    className="w-16 h-16 object-cover rounded border"
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
